import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, Package, Check, Square, CheckSquare, FileText, Download, 
  Share2, Printer, Trash2, Eye, Plus, Search, Filter, X, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const ProductCatalogGenerator = () => {
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewCatalog, setPreviewCatalog] = useState(null);
  
  const [newCatalog, setNewCatalog] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, catalogsRes, categoriesRes] = await Promise.all([
        axiosInstance.get('/products?active_only=false'),
        axiosInstance.get('/catalogs/product-catalogs'),
        axiosInstance.get('/categories')
      ]);
      
      setProducts(productsRes.data || []);
      setCatalogs(catalogsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const handleCreateCatalog = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Produkt aus');
      return;
    }
    
    if (!newCatalog.title.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }
    
    setCreating(true);
    
    try {
      const response = await axiosInstance.post('/catalogs/generate-product-catalog', {
        title: newCatalog.title,
        description: newCatalog.description,
        product_ids: selectedProducts
      });
      
      toast.success(response.data.message);
      setShowCreateDialog(false);
      setSelectedProducts([]);
      setNewCatalog({ title: '', description: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCatalog = async (catalogId) => {
    if (!window.confirm('Katalog wirklich löschen?')) return;
    
    try {
      await axiosInstance.delete(`/catalogs/product-catalogs/${catalogId}`);
      toast.success('Katalog gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handlePreviewCatalog = async (catalogId) => {
    try {
      const response = await axiosInstance.get(`/catalogs/product-catalogs/${catalogId}`);
      setPreviewCatalog(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const handlePrint = () => {
    // Open the catalog in a brand-new window for reliable, dialog-free printing.
    // Going via window.print() inside a Radix Dialog cuts content off (the dialog
    // body has overflow:auto and max-height — the print pipeline clips it).
    const node = document.getElementById('catalog-print');
    if (!node) {
      toast.error('Vorschau nicht bereit — bitte erneut versuchen');
      return;
    }
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) {
      toast.error('Bitte Popup-Blocker für homeyoursoul.com deaktivieren');
      return;
    }
    // Inline all the styles needed (Tailwind utility classes won't be in the new window)
    const stylesheets = Array.from(document.styleSheets)
      .map(ss => { try { return ss.href ? `<link rel="stylesheet" href="${ss.href}">` : ''; } catch { return ''; } })
      .join('\n');
    w.document.write(`<!doctype html>
<html lang="de">
  <head>
    <meta charset="utf-8">
    <title>${(previewCatalog?.title || 'Katalog').replace(/</g, '&lt;')} — Homeyoursoul</title>
    ${stylesheets}
    <style>
      @page { margin: 12mm; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .catalog-cover { page-break-after: always; break-after: page; }
      .grid > div { page-break-inside: avoid; break-inside: avoid; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>${node.outerHTML}</body>
</html>`);
    w.document.close();
    // wait for resources, then print
    w.onload = () => {
      setTimeout(() => { w.focus(); w.print(); }, 400);
    };
  };

  const handleShare = async (catalogId) => {
    if (!catalogId) {
      toast.error('Katalog konnte nicht geteilt werden');
      return;
    }
    // The dedicated public catalog viewer is not yet built — until then we
    // share the shop URL with the catalog title and product list so the
    // recipient knows what to look for.
    const productNames = (previewCatalog?.products || [])
      .slice(0, 8)
      .map(p => `· ${p.name}`)
      .join('\n');
    const more = (previewCatalog?.products?.length || 0) > 8
      ? `\n…und ${previewCatalog.products.length - 8} weitere`
      : '';
    const shopUrl = window.location.origin.replace(/\/admin.*$/, '');
    const shareData = {
      title: previewCatalog?.title || 'Homeyoursoul Katalog',
      text: `📚 ${previewCatalog?.title || 'Katalog'}\n${previewCatalog?.description || ''}\n\n${productNames}${more}\n\nShop: ${shopUrl}`,
      url: shopUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareData.text);
      toast.success('Katalog-Inhalt in Zwischenablage kopiert!');
    } catch {
      window.prompt('Katalog-Inhalt manuell kopieren:', shareData.text);
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = !q || (
      p.name?.toLowerCase().includes(q) ||
      (p.sku && p.sku.toLowerCase().includes(q)) ||
      (p.category && p.category.toLowerCase().includes(q)) ||
      (p.subcategory && p.subcategory.toLowerCase().includes(q)) ||
      (p.description && p.description.toLowerCase().includes(q)) ||
      (Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(q)))
    );
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Smart hint: if the search term matches a category exactly, suggest selecting it
  const categoryMatch = searchTerm.trim() && categories.find(c =>
    c.name?.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
    searchTerm.trim().toLowerCase().includes(c.name?.toLowerCase() || '')
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <Loader2 className="h-8 w-8 animate-spin text-[#AF9774]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 px-3" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Produkt-Katalog Generator" 
          subtitle="Erstellen Sie Produktkataloge zum Teilen und Drucken"
          icon={BookOpen}
        />

        {/* Existing Catalogs */}
        {catalogs.length > 0 && (
          <Card className="mb-6 rounded-none border" style={{ borderColor: '#C4A084' }}>
            <CardHeader className="pb-2" style={{ backgroundColor: '#AF9774' }}>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Erstellte Kataloge ({catalogs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid gap-3">
                {catalogs.map(catalog => (
                  <div 
                    key={catalog.id} 
                    className="flex items-center justify-between p-3 bg-white border rounded"
                    style={{ borderColor: '#D4C4B0' }}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium">{catalog.title}</h4>
                      <p className="text-sm text-gray-500">
                        {catalog.product_count} Produkte • {new Date(catalog.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handlePreviewCatalog(catalog.id)}
                        className="rounded-none"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleShare(catalog.id)}
                        className="rounded-none"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDeleteCatalog(catalog.id)}
                        className="rounded-none text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Selection */}
        <Card className="rounded-none border" style={{ borderColor: '#C4A084' }}>
          <CardHeader className="pb-2" style={{ backgroundColor: '#AF9774' }}>
            <CardTitle className="text-white text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Produkte auswählen
              </div>
              {selectedProducts.length > 0 && (
                <Badge className="bg-white text-[#AF9774]">
                  {selectedProducts.length} ausgewählt
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Filters — kompakte Pille im Brand-Style */}
            <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4 p-3" style={{ backgroundColor: '#F5EDE4', border: '1px solid #C4A084' }}>
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B7355] pointer-events-none" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, SKU, Kategorie, Tag oder Beschreibung…"
                  className="bg-white border-[#C4A084] rounded-none focus-visible:ring-[#AF9774]"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-white px-3 py-2 border border-[#C4A084] rounded-none text-sm min-w-[160px] focus:outline-none focus:ring-2 focus:ring-[#AF9774]"
              >
                <option value="">📁 Alle Kategorien</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              <Button
                variant="outline"
                onClick={handleSelectAll}
                className="rounded-none whitespace-nowrap border-[#AF9774] text-[#5D4E3C] hover:bg-[#AF9774] hover:text-white"
              >
                {filteredProducts.length > 0 && selectedProducts.length === filteredProducts.length ? (
                  <><CheckSquare className="h-4 w-4 mr-2" />Alle abwählen ({filteredProducts.length})</>
                ) : (
                  <><Square className="h-4 w-4 mr-2" />Alle auswählen ({filteredProducts.length})</>
                )}
              </Button>
              {(searchTerm || categoryFilter) && (
                <Button
                  variant="outline"
                  onClick={() => { setSearchTerm(''); setCategoryFilter(''); }}
                  className="rounded-none border-[#C4A084] text-[#8B7355] hover:bg-[#F5EDE4]"
                >
                  <X className="h-4 w-4 mr-1" /> Zurücksetzen
                </Button>
              )}
            </div>

            {/* Smart hint */}
            {categoryMatch && !categoryFilter && searchTerm && (
              <button
                type="button"
                onClick={() => { setCategoryFilter(categoryMatch.name); setSearchTerm(''); }}
                className="w-full mb-3 px-3 py-2 text-sm text-left bg-[#FAF5EE] border border-[#C4A084] hover:bg-[#F0E5D5] transition-colors"
              >
                💡 Tipp: Alle Produkte in der Kategorie <strong>„{categoryMatch.name}"</strong> auswählen?{' '}
                <span className="text-[#AF9774] underline">Jetzt filtern</span>
              </button>
            )}

            <p className="text-xs text-[#8B7355] mb-3 flex items-center gap-2 flex-wrap">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#AF9774' }} />
              <strong className="text-[#5D4E3C]">{filteredProducts.length}</strong> von {products.length} Produkten ·{' '}
              <strong className="text-[#5D4E3C]">{selectedProducts.length}</strong> ausgewählt
              <Button
                onClick={() => setShowCreateDialog(true)}
                disabled={selectedProducts.length === 0}
                className="ml-auto rounded-none text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: selectedProducts.length > 0 ? '#5D4E3C' : '#C4A084' }}
                size="sm"
                data-testid="create-catalog-top-btn"
                title={selectedProducts.length === 0 ? 'Erst Produkte auswählen' : 'Katalog erstellen'}
              >
                <Plus className="h-4 w-4 mr-2" />
                Katalog erstellen{selectedProducts.length > 0 ? ` (${selectedProducts.length})` : ''}
              </Button>
            </p>

            {/* Products Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[500px] overflow-y-auto p-1">
              {filteredProducts.map(product => {
                const isSelected = selectedProducts.includes(product.id);
                return (
                  <div
                    key={product.id}
                    onClick={() => handleSelectProduct(product.id)}
                    className={`cursor-pointer border-2 rounded overflow-hidden transition-all ${
                      isSelected ? 'border-[#AF9774] ring-2 ring-[#AF9774]/30' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="aspect-square bg-gray-100 relative">
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-8 w-8 text-gray-300" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-[#AF9774] rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                      {product.sale_price && product.sale_price < product.price && (
                        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded">
                          -{Math.round((1 - product.sale_price / product.price) * 100)}%
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium line-clamp-2">{product.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {product.sale_price && product.sale_price < product.price ? (
                          <>
                            <span className="text-xs line-through text-gray-400">
                              {formatEUR(product.price)}
                            </span>
                            <span className="text-sm font-bold text-red-600">
                              {formatEUR(product.sale_price)}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-bold">
                            {formatEUR(product.price)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Keine Produkte gefunden
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Button */}
        {selectedProducts.length > 0 && (
          <div
            className="fixed left-0 right-0 p-3 bg-white border-t shadow-lg z-50"
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 70px)' }}
          >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-3">
              <span className="font-medium text-sm text-[#5D4E3C] truncate">
                {selectedProducts.length} Produkte ausgewählt
              </span>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="rounded-none whitespace-nowrap text-white"
                style={{ backgroundColor: '#5D4E3C' }}
                data-testid="create-catalog-sticky-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Katalog erstellen
              </Button>
            </div>
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neuen Katalog erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Katalog-Titel *</Label>
                <Input
                  value={newCatalog.title}
                  onChange={(e) => setNewCatalog({...newCatalog, title: e.target.value})}
                  placeholder="z.B. Frühjahrskollektion 2026"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  value={newCatalog.description}
                  onChange={(e) => setNewCatalog({...newCatalog, description: e.target.value})}
                  placeholder="Kurze Beschreibung..."
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded text-sm">
                <strong>{selectedProducts.length} Produkte</strong> werden in den Katalog aufgenommen
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleCreateCatalog}
                  disabled={creating}
                  style={{ backgroundColor: '#AF9774' }}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Katalog erstellen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex flex-col items-start gap-3 pr-8">
                <span className="text-base sm:text-lg font-semibold truncate w-full">
                  {previewCatalog?.title}
                </span>
                <div className="flex gap-2 w-full">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePrint}
                    className="flex-1 sm:flex-none rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                    data-testid="catalog-print-btn"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Drucken
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleShare(previewCatalog?.id)}
                    className="flex-1 sm:flex-none rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#F5EDE4]"
                    data-testid="catalog-share-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Teilen
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {previewCatalog && (
              <div className="catalog-preview" id="catalog-print">
                {/* COVER PAGE — Brand-Style mit Logo, Ornamenten, Akzentfarben */}
                <div className="catalog-cover relative overflow-hidden" style={{
                  background: 'linear-gradient(135deg, #5D4E3C 0%, #6E5C48 50%, #AF9774 100%)',
                  color: '#F5EDE4',
                  minHeight: '420px',
                  padding: '50px 40px 60px',
                  textAlign: 'center',
                }}>
                  {/* Dekorative Ornamente */}
                  <div className="absolute top-4 left-4 text-3xl opacity-30">❀</div>
                  <div className="absolute top-4 right-4 text-3xl opacity-30">✦</div>
                  <div className="absolute bottom-4 left-4 text-3xl opacity-30">✦</div>
                  <div className="absolute bottom-4 right-4 text-3xl opacity-30">❀</div>

                  {/* Brand Logo */}
                  <img
                    src={`${window.location.origin}/logo-transparent.png`}
                    alt="Homeyoursoul"
                    className="mx-auto mb-4"
                    style={{ maxHeight: '80px', width: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.95 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="text-xs tracking-[6px] uppercase opacity-85 mb-2">Homeyoursoul</p>
                  <div className="w-12 h-[2px] mx-auto mb-6" style={{ backgroundColor: '#F5EDE4' }} />
                  <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                    {previewCatalog.title}
                  </h1>
                  {previewCatalog.description && (
                    <p className="max-w-xl mx-auto italic opacity-90 mb-6">{previewCatalog.description}</p>
                  )}
                  <div className="inline-block px-5 py-2 mt-4" style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(245,237,228,0.4)' }}>
                    <p className="text-xs tracking-[3px] uppercase">
                      ✦ {previewCatalog.product_count} ausgewählte Produkte ✦
                    </p>
                  </div>
                  <p className="text-xs opacity-70 mt-6">
                    Erstellt am {new Date(previewCatalog.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>

                {/* Sektions-Header */}
                <div className="my-6 px-6 flex items-center gap-3">
                  <div className="h-[1px] flex-1" style={{ backgroundColor: '#C4A084' }} />
                  <p className="text-xs tracking-[4px] uppercase text-[#8B7355]">❀ Produktübersicht ❀</p>
                  <div className="h-[1px] flex-1" style={{ backgroundColor: '#C4A084' }} />
                </div>

                {/* Produkt-Grid (gleich große Karten, einheitliche Bilder) */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 px-6 pb-6">
                  {previewCatalog.products?.map((product, idx) => (
                    <div
                      key={product.id}
                      className="flex flex-col overflow-hidden bg-white"
                      style={{ border: '1px solid #C4A084' }}
                    >
                      {/* Bild — einheitlich quadratisch, weißer Padding-Rand bei kleinen Bildern */}
                      <div
                        className="relative aspect-square flex items-center justify-center"
                        style={{ backgroundColor: '#FAF5EE' }}
                      >
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="h-12 w-12 text-[#C4A084]" />
                        )}
                        {/* Produkt-Index Badge */}
                        <span
                          className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5"
                          style={{ backgroundColor: '#5D4E3C', color: '#F5EDE4' }}
                        >
                          Nr. {String(idx + 1).padStart(2, '0')}
                        </span>
                      </div>
                      {/* Info-Bereich */}
                      <div className="flex flex-col flex-1 p-3" style={{ borderTop: '1px solid #C4A084' }}>
                        <h3 className="font-semibold text-sm leading-tight line-clamp-2 text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
                          {product.name}
                        </h3>
                        <p className="text-xs text-[#8B7355] mt-1.5 whitespace-pre-line">
                          {product.description || product.short_description || 'Liebevoll handverlesen für deinen besonderen Moment.'}
                        </p>
                        <div className="mt-auto pt-2" style={{ borderTop: '1px dashed #E0CFB8' }}>
                          {product.sale_price && product.sale_price < product.price ? (
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-xs line-through text-[#9C8C7A]">{formatEUR(product.price)}</span>
                              <span className="text-base font-bold text-red-600">{formatEUR(product.sale_price)}</span>
                              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5">
                                -{Math.round((1 - product.sale_price / product.price) * 100)}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-base font-bold text-[#5D4E3C]">{formatEUR(product.price)}</span>
                          )}
                          <p className="text-[10px] text-[#9C8C7A] mt-1 font-mono">
                            Art.-Nr.: <strong className="text-[#5D4E3C]">{product.sku || product.id?.slice(0, 8).toUpperCase() || '–'}</strong>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer mit Brand */}
                <div className="text-center py-6 px-6 mt-4" style={{ backgroundColor: '#F5EDE4', borderTop: '2px solid #C4A084' }}>
                  <p className="text-xs tracking-[4px] uppercase text-[#5D4E3C] mb-1">❀ Homeyoursoul ❀</p>
                  <p className="text-xs italic text-[#8B7355]">
                    {previewCatalog.product_count} liebevoll ausgewählte Produkte · www.homeyoursoul.com
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Print Styles — Cover-Seite immer eigenständig, Karten ohne Bruch.
          Also unconstrains the dialog when Ctrl+P is pressed directly. */}
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body * { visibility: hidden; }
          /* Override Radix Dialog constraints so the full catalog can flow */
          [role="dialog"] { max-height: none !important; overflow: visible !important; height: auto !important; }
          #catalog-print, #catalog-print * { visibility: visible; }
          #catalog-print { position: absolute; left: 0; top: 0; width: 100%; max-height: none !important; overflow: visible !important; }
          .catalog-cover { page-break-after: always; break-after: page; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #catalog-print > .grid > div { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default ProductCatalogGenerator;
